<template>
  <div v-if="user.isAnalytics()" class="reports-view">
    <div class="reports-header">
      <h1 class="reports-title">Proof of Play Report</h1>
      <ReportsToolbar
        :time-range="timeRange"
        :is-loading="isLoading"
        :is-dirty="isDirty"
        :is-stale="isStale"
        :has-response="!!proofOfPlayResponse"
        @update:time-range="timeRange = $event"
        @update="refreshReport"
        @export="showExportModal = true"
      >
        <template #filters>
          <PlayerFilterDialog @apply="onApplyPlayerFilter" />
          <MediaFilterDialog @apply="onApplyMediaFilter" />
        </template>
      </ReportsToolbar>
    </div>

    <div v-if="isDirty" class="rpt-stale-banner">
      <svg class="rpt-stale-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      Filters changed &mdash; click <strong>Update</strong> to refresh the report.
    </div>

    <div v-else-if="isStale" class="rpt-stale-banner">
      <svg class="rpt-stale-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      Data may be out of date &mdash; click <strong>Update</strong> to refresh the report.
    </div>

    <div
      v-if="proofOfPlayResponse && proofOfPlayResponse.timeRange.startTime"
      class="rpt-time-info"
    >
      <span class="rpt-time-label">{{ proofOfPlayResponse.timeRange.label }}</span>
      <span class="rpt-time-range"
        >{{ formatDateTimeUTC(proofOfPlayResponse.timeRange.startTime) }} —
        {{ formatDateTimeUTC(proofOfPlayResponse.timeRange.endTime) }} (UTC)</span
      >
    </div>

    <div v-if="isLoading && !proofOfPlayResponse" class="rpt-loading">
      <span class="rpt-spinner rpt-spinner-lg" />
      <span>Loading report data...</span>
    </div>

    <div v-if="error" class="rpt-error">
      <p>{{ error }}</p>
      <button class="rpt-btn rpt-btn-primary" @click="refreshReport">Retry</button>
    </div>

    <template v-if="proofOfPlayResponse">
      <SummaryCards
        :summary="proofOfPlayResponse.summary"
        :avgPlaysPerPlayer="avgPlaysPerPlayer"
        :peakActivityTime="peakActivityTime"
      />

      <div
        v-if="proofOfPlayResponse.timeSeries.length || topMediaList.length"
        class="rpt-trend-row"
      >
        <TrendChart
          v-if="proofOfPlayResponse.timeSeries.length"
          class="rpt-trend-chart"
          :cumulativeData="cumulativeData"
          :cumulativeMax="cumulativeMax"
          :shortBucketLabel="shortBucketLabel"
          @export-trend="exportSectionTrend"
        />

        <TopPlayedList
          v-if="topMediaList.length"
          class="rpt-trend-top"
          :items="topMediaList"
          :thumbMap="thumbMap"
        />
      </div>

      <ByMediaTable
        :items="proofOfPlayResponse.byMedia"
        :totalCount="mediaTotalCount"
        :isLoadingMore="isLoadingMoreMedia"
        :hasMore="hasMoreMedia"
        :thumbMap="thumbMap"
        @load-more="loadMoreMedia"
        @export-section="exportSectionMedia"
      />

      <ByPlayerTable
        :items="proofOfPlayResponse.byPlayer"
        :totalCount="playerTotalCount"
        :isLoadingMore="isLoadingMorePlayers"
        :hasMore="hasMorePlayers"
        :playerInfoByUuid="playerInfoByUuid"
        @load-more="loadMorePlayers"
        @export-section="exportSectionPlayer"
      />

      <div
        v-if="
          !proofOfPlayResponse.byMedia.length &&
          !proofOfPlayResponse.byPlayer.length &&
          !proofOfPlayResponse.timeSeries.length
        "
        class="rpt-empty"
      >
        <div class="rpt-empty-icon">📊</div>
        <p v-if="isWindowEmpty">
          No completed buckets in this window yet — check back after the next bucket closes.
        </p>
        <p v-else>No playback data available for the selected filters and time range.</p>
      </div>
    </template>

    <ExportModal
      :show="showExportModal"
      @close="showExportModal = false"
      @export="handleExport"
    />
  </div>

  <div v-else class="reports-no-access">
    <p>You do not have access to reports.</p>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import type { ProofOfPlayTimeRangePreset } from "@/helpers/api/metrics";

import useUserStore from "@/stores/user";
import { useReportData } from "./composables/useReportData";
import { useReportExport, type ExportSections } from "./composables/useReportExport";
import { useCumulativePlayChart } from "./composables/useCumulativePlayChart";
import { useReportThumbnails } from "./composables/useReportThumbnails";
import { usePlayerInfoLookup } from "./composables/usePlayerInfoLookup";

import ReportsToolbar from "./components/ReportsToolbar.vue";
import PlayerFilterDialog from "./components/PlayerFilterDialog.vue";
import MediaFilterDialog from "./components/MediaFilterDialog.vue";
import ExportModal from "./components/ExportModal.vue";
import ByMediaTable from "./components/ByMediaTable.vue";
import ByPlayerTable from "./components/ByPlayerTable.vue";
import SummaryCards from "./components/SummaryCards.vue";
import TrendChart from "./components/TrendChart.vue";
import TopPlayedList from "./components/TopPlayedList.vue";

const user = useUserStore();

const timeRange = ref<ProofOfPlayTimeRangePreset>("today");
const selectedPlayerUuids = ref<string[]>([]);
const selectedMediaIds = ref<string[]>([]);
const showExportModal = ref(false);

const {
  proofOfPlayResponse,
  isLoading,
  error,
  mediaTotalCount,
  playerTotalCount,
  isLoadingMoreMedia,
  isLoadingMorePlayers,
  hasMoreMedia,
  hasMorePlayers,
  isDirty,
  isStale,
  fetchData,
  buildCurrentFilterParams,
  loadMoreMedia,
  loadMorePlayers,
} = useReportData(timeRange, selectedPlayerUuids, selectedMediaIds);

const { shortBucketLabel, cumulativeData, cumulativeMax } =
  useCumulativePlayChart(proofOfPlayResponse, timeRange);

const { thumbMap, clearFailedThumbnails } = useReportThumbnails(
  () => proofOfPlayResponse.value?.byMedia,
);

const { playerInfoByUuid } = usePlayerInfoLookup(
  () => proofOfPlayResponse.value?.byPlayer,
);

const { exportToExcel, exportSectionMedia, exportSectionPlayer, exportSectionTrend } =
  useReportExport(proofOfPlayResponse, buildCurrentFilterParams);

const topMediaList = computed(
  () => proofOfPlayResponse.value?.byMedia.slice(0, 3) ?? [],
);

const avgPlaysPerPlayer = computed(() => {
  const s = proofOfPlayResponse.value?.summary;
  if (!s || !s.unique_player_count) return "0";
  return Math.round(s.total_plays / s.unique_player_count).toLocaleString();
});

// today / quarter / ytd can legitimately return an empty window when no bucket
// has closed yet for the current period (e.g. today at 00:30 — the first hourly
// bucket closes at 01:00). Detect this by comparing the floored start/end times
// so we can show "check back after the next bucket closes" instead of a generic
// "no data" message.
const isWindowEmpty = computed(() => {
  const tr = proofOfPlayResponse.value?.timeRange;
  if (!tr?.startTime || !tr?.endTime) return false;
  return tr.startTime === tr.endTime;
});

const peakActivityTime = computed(() => {
  const ts = proofOfPlayResponse.value?.timeSeries;
  if (!ts || !ts.length) return "—";
  let maxPlays = 0;
  let peakBucket = "";
  for (const b of ts) {
    if (b.total_plays > maxPlays) {
      maxPlays = b.total_plays;
      peakBucket = b.bucket;
    }
  }
  return peakBucket ? shortBucketLabel(peakBucket) : "—";
});

function formatDateTimeUTC(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { timeZone: "UTC" });
  } catch {
    return iso;
  }
}

function onApplyPlayerFilter(uuids: string[]) {
  selectedPlayerUuids.value = uuids;
}

function onApplyMediaFilter(ids: string[]) {
  selectedMediaIds.value = ids;
}

async function handleExport(sections: ExportSections) {
  await exportToExcel(sections);
  showExportModal.value = false;
}

async function refreshReport() {
  // Give previously-failed thumbnail lookups another chance.
  clearFailedThumbnails();
  await fetchData();
}

onMounted(async () => {
  await fetchData();
});
</script>

<style scoped>
@import '@/views/ReportsView/styles/reports-shared.css';

.reports-view {
  padding: 1.5rem 0;
  max-width: 100%;
}

.reports-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.reports-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.rpt-stale-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.5rem;
  color: #92400e;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}
.rpt-stale-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  stroke: #d97706;
}

.rpt-time-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: #0369a1;
}

.rpt-time-label {
  font-weight: 600;
}

.rpt-time-range {
  color: #0c4a6e;
}

.rpt-time-hint {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  font-size: 0.75rem;
  font-style: italic;
}

.rpt-spinner-lg {
  width: 24px;
  height: 24px;
  border-width: 3px;
}

.rpt-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: #6b7280;
  font-size: 1rem;
}

.rpt-error {
  padding: 1.5rem;
  text-align: center;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.rpt-error p {
  margin: 0 0 0.75rem;
}

.rpt-trend-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.rpt-trend-chart {
  flex: 7;
  min-width: 0;
  margin-bottom: 0;
  overflow: hidden;
}

.rpt-trend-top {
  flex: 3;
  min-width: 0;
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .rpt-trend-row {
    flex-direction: column;
  }
}

.rpt-empty {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.rpt-empty-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.rpt-empty p {
  font-size: 1rem;
  margin: 0;
}

.reports-no-access {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
  font-size: 1.1rem;
}
</style>
